/**
 * SymbolItem Component
 * 
 * @purpose Renders a single symbol in the list with:
 *   - Clickable symbol text (toggles filter)
 *   - Hover popup with edit/delete buttons
 *   - Visual styling for filtered state
 * 
 * @relates 
 *   - InfoSymbolPickerHeader.tsx - Handlers for filter toggle, edit, delete
 */

import React, { useEffect, useRef, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { SymbolChannels } from '../../types';
import { FavoriteButton } from '../../components/blocks/FavoriteButton';
import { AssetSymbolInfo, SymbolTypes } from '@tradingblock/types';
import { toAssetSymbolBySymbolType, isIndexSymbol } from '@tradingblock/api';
import { useFavorite } from '../../hooks/useFavorite';

/**
 * Props interface for SymbolItem
 */
interface SymbolItemProps {
    symbol: SymbolChannels; // Symbol data including name and channels
    index: number; // Index in the list (for separator rendering)
    total: number; // Total number of symbols (for separator rendering)
    isFiltered: boolean; // Whether this symbol is currently filtered
    hasActiveFilter: boolean; // Whether any filter is currently active
    isHovered: boolean; // Whether this symbol is currently hovered (for popup display)
    symbolRef: (el: HTMLSpanElement | null) => void; // Callback to store ref for width calculation
    onMouseEnter: (e: React.MouseEvent) => void; // Handler when mouse enters symbol
    onMouseLeave: () => void; // Handler when mouse leaves symbol
    onToggleFilter: (symbolName: string, e: React.MouseEvent) => void; // Handler to toggle filter state
    onEditChannels: (symbol: SymbolChannels, e: React.MouseEvent) => void; // Handler to open channel edit modal
    onDeleteSymbol: (symbolName: string, e: React.MouseEvent) => void; // Handler to delete symbol
    onPopupMouseEnter: (e: React.MouseEvent) => void; // Handler when mouse enters popup
}

export const SymbolItem: React.FunctionComponent<SymbolItemProps> = ({
    symbol,
    index,
    total,
    isFiltered,
    hasActiveFilter,
    isHovered,
    symbolRef,
    onMouseEnter,
    onMouseLeave,
    onToggleFilter,
    onEditChannels,
    onDeleteSymbol,
    onPopupMouseEnter,
}) => {
    /**
     * Component State and Refs
     * 
     * @purpose Manages popup positioning and element references
     */
    const isLastSymbol = index === total - 1; // Whether this is the last symbol (no separator after)
    const popupRef = useRef<HTMLDivElement>(null); // Ref to popup element
    const symbolElementRef = useRef<HTMLSpanElement | null>(null); // Ref to symbol element for positioning
    const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null); // Calculated popup position

    /**
     * Calculate Popup Position
     * 
     * @purpose Calculates fixed position for popup below symbol when hovered
     * @logic Uses getBoundingClientRect to get symbol position, adds scroll offsets
     * @relates React Portal - Popup rendered to document.body with fixed positioning
     */
    useEffect(() => {
        if (isHovered && symbolElementRef.current) {
            const rect = symbolElementRef.current.getBoundingClientRect();
            setPopupPosition({
                top: rect.bottom + window.scrollY + 4, // Position below symbol, 4px gap
                left: rect.left + rect.width / 2 + window.scrollX, // Centered horizontally
            });
        } else {
            setPopupPosition(null);
        }
    }, [isHovered]);

    /**
     * Create AssetSymbolInfo for FavoriteButton
     * 
     * @purpose Creates a proper AssetSymbolInfo object from symbol name for FavoriteButton
     * @logic Uses toAssetSymbolBySymbolType to create a complete AssetSymbolInfo with all required properties
     * @relates FavoriteButton - Requires complete AssetSymbolInfo to add/remove favorites
     * @note Uses useMemo with symbol.name as dependency to maintain stable reference and prevent infinite loops
     */
    const assetSymbolInfo: AssetSymbolInfo = useMemo(() => {
        const symbolType = isIndexSymbol(symbol.name) ? SymbolTypes.index : SymbolTypes.stock;
        return toAssetSymbolBySymbolType(symbol.name, symbolType, {});
    }, [symbol.name]);

    const [isFavorited] = useFavorite(assetSymbolInfo);

    /**
     * Popup Content (Edit/Delete/Favorite Buttons)
     * 
     * @purpose Renders popup with edit, delete, and favorite buttons when symbol is hovered
     * @logic 
     *   - Only renders when isHovered is true and position is calculated
     *   - Uses React Portal to render outside DOM hierarchy (prevents clipping)
     *   - Positioned below symbol using fixed positioning
     * @relates ReactDOM.createPortal - Renders popup to document.body
     * @relates InfoSymbolPickerHeader.tsx - Handlers for edit/delete actions
     * @relates FavoriteButton - Component for favoriting/unfavoriting symbols
     */
    const popupContent = isHovered && popupPosition ? (
        <div
            ref={popupRef}
            className="symbol-popup"
            data-symbol-popup="true" // Data attribute to detect popup clicks
            style={{
                position: 'fixed',
                top: `${popupPosition.top}px`,
                left: `${popupPosition.left}px`,
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
            onMouseEnter={e => {
                e.stopPropagation();
                onPopupMouseEnter(e);
            }}
            onMouseLeave={() => {
                onMouseLeave();
            }}
            onClick={e => {
                e.stopPropagation();
            }}
        >
            <span className="symbol-popup-pointer" aria-hidden />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {/* Edit Channels Button */}
                <button
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEditChannels(symbol, e);
                        onMouseLeave();
                    }}
                    title={`Edit channels for ${symbol.name}`}
                >
                    <i className="fal fa-pencil" />
                </button>
                {/* Favorite Button */}
                <div
                    onClick={e => {
                        e.stopPropagation();
                    }}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    title={isFavorited ? `Remove ${symbol.name} from favorites` : `Add ${symbol.name} to favorites`}
                >
                    <FavoriteButton symbol={assetSymbolInfo} />
                </div>
                {/* Delete Symbol Button */}
                <button
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDeleteSymbol(symbol.name, e);
                        onMouseLeave();
                    }}
                    title={`Remove ${symbol.name} from subscription`}
                >
                    <i className="fal fa-trash-alt" />
                </button>
            </div>
        </div>
    ) : null;

    return (
        <React.Fragment key={symbol.name}>
            {/* 
                Symbol Text Element
                @purpose Clickable symbol text that toggles filter state
                @logic 
                  - Stores ref for width calculation and popup positioning
                  - Prevents filter toggle when clicking popup
                  - Shows blue color when filtered
                @relates 
                  - symbolRef callback - Stores ref in parent for width calculation
                  - onToggleFilter - Called when symbol is clicked
            */}
            <span
                ref={el => {
                    symbolElementRef.current = el; // Store locally for popup positioning
                    symbolRef(el); // Pass to parent for width calculation
                    if (el) {
                        el.setAttribute('data-symbol-name', symbol.name); // Data attribute for identification
                    }
                }}
                className="showing-symbol-item"
                onMouseEnter={onMouseEnter} // Show popup on hover
                onMouseLeave={onMouseLeave} // Hide popup on leave
                onClick={e => {
                    const target = e.target as HTMLElement;
                    const popup = target.closest('[data-symbol-popup]'); // Check if clicking popup
                    if (!popup) {
                        onToggleFilter(symbol.name, e); // Toggle filter if not clicking popup
                    }
                }}
                style={{
                    cursor: 'pointer',
                }}
                title={isFiltered ? `Click to remove ${symbol.name} from filter` : `Click to filter by ${symbol.name}`}
            >
                {/* Inner span with pointer-events: none to prevent text from blocking clicks */}
                <span
                    style={{
                        textDecoration: 'none',
                        fontWeight: 'normal',
                        opacity: hasActiveFilter && !isFiltered ? 0.4 : 1,
                        pointerEvents: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        transition: 'opacity 0.2s ease',
                    }}
                >
                    {symbol.name}
                </span>
            </span>
            {/* Separator between symbols (not after last) */}
            {!isLastSymbol && ' - '}
            {/* Render popup via Portal to document.body to prevent clipping */}
            {popupContent && ReactDOM.createPortal(popupContent, document.body)}
        </React.Fragment>
    );
};

